import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  FaBold,
  FaImage,
  FaItalic,
  FaLink,
  FaParagraph,
  FaQuoteRight,
  FaUnderline,
  FaVideo,
} from "react-icons/fa";
import { GoListOrdered } from "react-icons/go";
import { RiListUnordered } from "react-icons/ri";
import swal from "sweetalert";
import { BASE_URL_API_BLOG as BASE_URL } from "../Utils/Constant";
import { basicauthblog } from "../Utils/Credentials";
import "../../assets/styles/components/_bumppy-edit-blog.scss";

const valueFrom = (blog, ...keys) => {
  const key = keys.find((name) => blog?.[name] !== undefined && blog?.[name] !== null);
  return key ? blog[key] : "";
};

const BumppyEditBlog = () => {
  const history = useHistory();
  const editorRef = useRef(null);
  const selectedBlog = JSON.parse(sessionStorage.getItem("Editblogid") || "null");
  const oldSlug = valueFrom(selectedBlog, "slug", "post_slug", "post_name");

  const [title, setTitle] = useState(valueFrom(selectedBlog, "title", "post_title"));
  const [slug, setSlug] = useState(oldSlug);
  const [excerpt, setExcerpt] = useState(
    valueFrom(selectedBlog, "excerpt", "post_excerpt")
  );
  const [thumbnail, setThumbnail] = useState(
    valueFrom(
      selectedBlog,
      "feature_url",
      "thumbnail",
      "image_url",
      "post_featured_image",
      "featured_image"
    )
  );
  const [category, setCategory] = useState(
    valueFrom(
      selectedBlog,
      "category_name",
      "category",
      "category_id",
      "post_category"
    )
  );
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState("");
  const [isLoadingBlog, setIsLoadingBlog] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const getHeaders = () => {
    const { username, password } = basicauthblog;
    return {
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      "Content-Type": "application/json",
    };
  };

  const loadBlogDetails = async () => {
    if (!oldSlug) {
      setLoadError("The selected blog does not contain a slug.");
      setIsLoadingBlog(false);
      return;
    }

    setIsLoadingBlog(true);
    setLoadError("");
    try {
      const response = await fetch(`${BASE_URL}/GetBlogBySlug`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ single_post_id: oldSlug }),
      });
      const result = await response.json();

      if (!response.ok || String(result.status) !== "0") {
        throw new Error(result.msg || "Unable to load the selected blog.");
      }

      const blog = Array.isArray(result.content) ? result.content[0] : null;
      if (!blog) throw new Error("The blog details response was empty.");

      setTitle(valueFrom(blog, "post_title", "title"));
      setSlug(valueFrom(blog, "post_name", "post_slug", "slug") || oldSlug);
      setExcerpt(valueFrom(blog, "post_excerpt", "excerpt"));
      setThumbnail(
        valueFrom(blog, "thumbnail", "feature_url", "image_url", "featured_image")
      );
      setCategory(valueFrom(blog, "category", "category_name"));

      setContent(valueFrom(blog, "post_content", "content"));
    } catch (error) {
      setLoadError(error.message || "Something went wrong while loading the blog.");
    } finally {
      setIsLoadingBlog(false);
    }
  };

  useEffect(() => {
    loadBlogDetails();
    // The selected slug is captured when this edit screen opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetch(`${BASE_URL}/GetBlogCategories`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ type: "0" }),
        });
        const result = await response.json();
        if (response.ok && String(result.status) === "0") {
          const categoryList = Array.isArray(result.data) ? result.data : [];
          setCategories(categoryList);
          const selectedCategory = categoryList.find(
            (item) =>
              String(item.id) === String(category) ||
              item.category_name?.toLowerCase() === String(category).toLowerCase()
          );
          if (selectedCategory) setCategory(selectedCategory.category_name);
        }
      } catch (error) {
        console.error("Unable to load blog categories", error);
      }
    };
    loadCategories();
  }, []);

  const formatText = (command, value = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(command, false, value);
  };

  const insertLink = () => {
    const url = window.prompt("Enter the URL");
    if (url) formatText("createLink", url);
  };

  const insertImage = () => {
    const url = window.prompt("Enter the image URL");
    if (url) formatText("insertImage", url);
  };

  const insertVideo = () => {
    const input = window.prompt("Enter a YouTube URL or video ID");
    if (!input) return;
    let videoId = input.trim();
    try {
      const url = new URL(input);
      videoId = url.hostname === "youtu.be"
        ? url.pathname.slice(1)
        : url.searchParams.get("v") || url.pathname.split("/").pop();
    } catch (error) {
      // A plain YouTube video ID is also accepted.
    }
    videoId = videoId.replace(/[^a-zA-Z0-9_-]/g, "");
    formatText(
      "insertHTML",
      `<iframe src="https://www.youtube.com/embed/${videoId}" width="560" height="315" frameborder="0" allowfullscreen></iframe>`
    );
  };

  const updateBlog = async (event) => {
    event.preventDefault();
    const content = editorRef.current?.innerHTML.trim() || "";

    if (!oldSlug || !title.trim() || !slug.trim() || !content || !category) {
      swal("Missing information", "Title, slug, category, and content are required.", "warning");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/ModifyBlogBySlug`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          old_slug: oldSlug,
          post_title: title.trim(),
          post_content: content,
          post_excerpt: excerpt.trim(),
          new_slug: slug.trim().replace(/^\/+|\/+$/g, ""),
          thumbnail: thumbnail.trim(),
          category,
        }),
      });
      const result = await response.json();
      if (!response.ok || String(result.status) !== "0") {
        throw new Error(result.msg || "Unable to update the blog.");
      }

      swal("Updated", result.msg || "Bumppy blog updated successfully.", "success");
      history.push("/blog/BumppyBlogs");
    } catch (error) {
      swal("Update failed", error.message || "Something went wrong.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedBlog) {
    return (
      <div className="bumppy-edit-empty">
        <h3>No blog selected</h3>
        <p>Choose a blog from the Bumppy Blogs table before editing.</p>
        <Link to="/blog/BumppyBlogs">Return to Bumppy Blogs</Link>
      </div>
    );
  }

  if (isLoadingBlog) {
    return (
      <div className="bumppy-edit-loading">
        <Spinner animation="border" />
        <span>Loading complete blog details...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bumppy-edit-empty">
        <h3>Unable to load blog</h3>
        <p>{loadError}</p>
        <button type="button" onClick={loadBlogDetails}>Try again</button>
        <Link to="/blog/BumppyBlogs">Return to Bumppy Blogs</Link>
      </div>
    );
  }

  return (
    <main className="bumppy-edit-page">
      <header className="edit-page-header">
        <div>
          <span>Edit article</span>
          <h2>{title || "Bumppy Blog"}</h2>
        </div>
        <div className="header-actions">
          <Link to="/blog/BumppyBlogs" className="cancel-button">Cancel</Link>
          <button type="submit" form="bumppy-edit-form" disabled={isSaving}>
            {isSaving ? <><Spinner size="sm" animation="border" /> Saving...</> : "Update blog"}
          </button>
        </div>
      </header>

      <form id="bumppy-edit-form" onSubmit={updateBlog} className="bumppy-edit-form">
        <section className="edit-main-card">
          <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <div className="two-column-fields">
            {/* <label>Slug<input value={slug} onChange={(e) => setSlug(e.target.value)} /></label> */}
            {/* <label>Category
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Select category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.category_name}>{item.category_name}</option>
                ))}
              </select>
            </label> */}
          </div>
          {/* <label>Excerpt<textarea rows="3" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></label> */}

          <label className="content-label">Content</label>
          <div className="bumppy-editor-toolbar">
            {[1, 2, 3, 4, 5, 6].map((level) => (
              <button type="button" key={level} onClick={() => formatText("formatBlock", `<h${level}>`)}>H{level}</button>
            ))}
            <button type="button" onClick={() => formatText("formatBlock", "<blockquote>")} title="Quote"><FaQuoteRight /></button>
            <button type="button" onClick={() => formatText("formatBlock", "<p>")} title="Paragraph"><FaParagraph /></button>
            <button type="button" onClick={() => formatText("insertUnorderedList")} title="Bulleted list"><RiListUnordered /></button>
            <button type="button" onClick={() => formatText("insertOrderedList")} title="Numbered list"><GoListOrdered /></button>
            <button type="button" onClick={() => formatText("bold")} title="Bold"><FaBold /></button>
            <button type="button" onClick={() => formatText("italic")} title="Italic"><FaItalic /></button>
            <button type="button" onClick={() => formatText("underline")} title="Underline"><FaUnderline /></button>
            <input type="color" aria-label="Text color" onChange={(e) => formatText("foreColor", e.target.value)} />
            <button type="button" onClick={insertLink} title="Link"><FaLink /></button>
            <button type="button" onClick={insertImage} title="Image"><FaImage /></button>
            <button type="button" onClick={insertVideo} title="YouTube video"><FaVideo /></button>
          </div>
          <div
            className="bumppy-content-editor"
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </section>

        <aside className="edit-side-card">
          <label>Thumbnail URL<input value={thumbnail} onChange={(e) => setThumbnail(e.target.value)} /></label>
          {thumbnail && <img src={thumbnail} alt="Blog thumbnail preview" />}
          <div className="slug-preview"><span>Public URL</span><a href={`https://bumppy.com/${slug}`} target="_blank" rel="noopener noreferrer">bumppy.com/{slug}</a></div>
        </aside>
      </form>
    </main>
  );
};

export default BumppyEditBlog;
